// Shared sort-value mapping for Algolia query building. Lives in a
// neutral module (no `'use client'` / `'server-only'` directives) so
// both `useAlgoliaProducts` (client) and `server.ts` (server) can
// import the same source of truth and resolve identical replica index
// names from the same FE sort value.

/**
 * Sort options the hook understands. Recommended = primary (relevance)
 * index. Other values map to virtual/standard replicas — sent to Algolia
 * by switching the indexName suffix (e.g. `dev_products_sdau_price_asc`).
 * Callers pass the FE-canonical hyphenated form; resolution maps that
 * to the underscore-keyed replica suffix expected by `getProductsIndexName`.
 */
export type AlgoliaSortValue = 'recommended' | 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc' | 'newest';

export type AlgoliaSortReplicaSuffix =
  | 'name_asc'
  | 'name_desc'
  | 'price_asc'
  | 'price_desc'
  // EWM-255 — Newest sort. Maps to the virtual replica
  // `{env}_products_sd{market}_createdat_desc`.
  //
  // EWM-686 (2026-09-17) — corrected against the live index settings. This
  // comment used to say the replica is custom-ranked `isAvailable desc,
  // createdAt desc` ("in-stock newest first"). That matched neither
  // environment. Measured via `GET /1/indexes/<replica>/settings`:
  //   * prod, both markets: `customRanking: ['desc(createdAt)']` — no
  //     `isAvailable` term at all;
  //   * dev, both markets:  `customRanking: ['desc(createdAt)', 'desc(isAvailable)']`
  //     — `createdAt` first, `isAvailable` only as a tie-breaker.
  // The two environments have drifted and nothing in this repo asserts
  // settings parity (raised as a follow-up on EWM-686).
  //
  // "In-stock newest first" was wrong twice over, because `isAvailable` is not
  // a stock flag: it is `false` if and only if `lifeCycleAttribute` is "GO",
  // which `buildAlgoliaFilters` already excludes from every browse/search
  // query (EWM-643). Inside the set a shopper can see it is constant `true`,
  // so even dev's tie-breaker reorders nothing. This sort is newest-first,
  // full stop. See `algolia.record.in-stock-attr-discriminates` in
  // `docs/DATA-ASSUMPTIONS.md`.
  | 'createdat_desc';

export const SORT_TO_REPLICA_SUFFIX: Record<Exclude<AlgoliaSortValue, 'recommended'>, AlgoliaSortReplicaSuffix> = {
  'price-asc': 'price_asc',
  'price-desc': 'price_desc',
  'name-asc': 'name_asc',
  'name-desc': 'name_desc',
  newest: 'createdat_desc',
};

/** Resolve replica suffix for `getProductsIndexName`. Undefined → primary index. */
export const resolveReplicaSuffix = (sort: AlgoliaSortValue): AlgoliaSortReplicaSuffix | undefined =>
  sort === 'recommended' ? undefined : SORT_TO_REPLICA_SUFFIX[sort];
